/**
 * カラーパレット定義 - TypeScript版
 * CSS変数と同期させることで、JS側からも色を参照可能にする
 */

export type ThemeName = 'light' | 'dark';

export interface ColorPalette {
  // 背景色
  bgPage: string;
  bgSurface: string;
  bgHeader: string;
  bgFooter: string;
  bgOverlay: string;
  bgHover: string;
  bgActive: string;

  // テキスト色
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  textMuted: string;

  // ボーダー色
  border: string;
  borderSubtle: string;
  borderStrong: string;

  // アクセント色
  accent: string;
  accentHover: string;
  accentLight: string;
  accentLighter: string;
  accentDark: string;
  accentBg: string;

  // ボタン色
  btnPrimaryBg: string;
  btnPrimaryText: string;
  btnPrimaryHover: string;
  btnSecondaryBg: string;
  btnSecondaryText: string;
  btnSecondaryHover: string;
  btnDangerBg: string;
  btnDangerText: string;
  btnDangerHover: string;

  // ステータス色
  success: string;
  warning: string;
  error: string;
  info: string;

  // フォーカス・ホバー
  focusRing: string;
  hoverOverlay: string;

  // スクロールバー
  scrollbarTrack: string;
  scrollbarThumb: string;
  scrollbarThumbHover: string;

  // 選択ハイライト
  selectionBg: string;
  selectionText: string;
}

export const lightThemeColors: ColorPalette = {
  // 背景色
  bgPage: '#f5f5f7',
  bgSurface: '#ffffff',
  bgHeader: '#ffffff',
  bgFooter: '#f9fafb',
  bgOverlay: 'rgba(0, 0, 0, 0.5)',
  bgHover: 'rgba(0, 0, 0, 0.05)',
  bgActive: 'rgba(0, 0, 0, 0.1)',

  // テキスト色
  textPrimary: '#111827',
  textSecondary: '#4b5563',
  textTertiary: '#6b7280',
  textInverse: '#ffffff',
  textMuted: '#9ca3af',

  // ボーダー色
  border: '#e5e7eb',
  borderSubtle: '#f3f4f6',
  borderStrong: '#d1d5db',

  // アクセント色
  accent: '#4f46e5',
  accentHover: '#4338ca',
  accentLight: '#6366f1',
  accentLighter: '#818cf8',
  accentDark: '#4338ca',
  accentBg: 'rgba(79, 70, 229, 0.1)',

  // ボタン色
  btnPrimaryBg: '#4f46e5',
  btnPrimaryText: '#ffffff',
  btnPrimaryHover: '#4338ca',
  btnSecondaryBg: '#f3f4f6',
  btnSecondaryText: '#111827',
  btnSecondaryHover: '#e5e7eb',
  btnDangerBg: '#ef4444',
  btnDangerText: '#ffffff',
  btnDangerHover: '#dc2626',

  // ステータス色
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',

  // フォーカス・ホバー
  focusRing: 'rgba(79, 70, 229, 0.5)',
  hoverOverlay: 'rgba(0, 0, 0, 0.05)',

  // スクロールバー
  scrollbarTrack: '#f1f1f1',
  scrollbarThumb: '#cbd5e1',
  scrollbarThumbHover: '#94a3b8',

  // 選択ハイライト
  selectionBg: 'rgba(79, 70, 229, 0.2)',
  selectionText: '#111827',
};

export const darkThemeColors: ColorPalette = {
  // 背景色
  bgPage: '#111827',
  bgSurface: '#1f2937',
  bgHeader: '#1f2937',
  bgFooter: '#111827',
  bgOverlay: 'rgba(0, 0, 0, 0.7)',
  bgHover: 'rgba(255, 255, 255, 0.1)',
  bgActive: 'rgba(255, 255, 255, 0.15)',

  // テキスト色
  textPrimary: '#e5e7eb',
  textSecondary: '#d1d5db',
  textTertiary: '#9ca3af',
  textInverse: '#111827',
  textMuted: '#6b7280',

  // ボーダー色
  border: '#374151',
  borderSubtle: '#4b5563',
  borderStrong: '#6b7280',

  // アクセント色
  accent: '#6366f1',
  accentHover: '#818cf8',
  accentLight: '#818cf8',
  accentLighter: '#a5b4fc',
  accentDark: '#4f46e5',
  accentBg: 'rgba(99, 102, 241, 0.15)',

  // ボタン色
  btnPrimaryBg: '#6366f1',
  btnPrimaryText: '#ffffff',
  btnPrimaryHover: '#818cf8',
  btnSecondaryBg: '#374151',
  btnSecondaryText: '#e5e7eb',
  btnSecondaryHover: '#4b5563',
  btnDangerBg: '#ef4444',
  btnDangerText: '#ffffff',
  btnDangerHover: '#f87171',

  // ステータス色
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',

  // フォーカス・ホバー
  focusRing: 'rgba(99, 102, 241, 0.5)',
  hoverOverlay: 'rgba(255, 255, 255, 0.1)',

  // スクロールバー
  scrollbarTrack: '#1f2937',
  scrollbarThumb: '#4b5563',
  scrollbarThumbHover: '#6b7280',

  // 選択ハイライト
  selectionBg: 'rgba(99, 102, 241, 0.3)',
  selectionText: '#e5e7eb',
};

/**
 * テーマ名からカラーパレットを取得
 */
export const getThemeColors = (theme: ThemeName): ColorPalette => {
  return theme === 'dark' ? darkThemeColors : lightThemeColors;
};

/**
 * システムのダークモード設定を検出
 */
export const getSystemTheme = (): ThemeName => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

